import React, { useState, useRef, useCallback, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { useChat } from '../../context/ChatContext.jsx';
import ModeSwitch from '../common/ModeSwitch.jsx';

const IndividualSidebar = ({ open, collapsed, toggleSidebar, toggleCollapse }) => {
  const { hasRole } = useAuth();
  const { totalUnread } = useChat();
  const [showTooltip, setShowTooltip] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef(null);

  const standardWidth = 191; // Reduced by 8% from 208px (w-52)
  const collapsedWidth = 64; // w-16 = 64px

  // Individual/Personal navigation - restored from original Sidebar.jsx
  const navigation = [
    { 
      name: 'Dashboard',
      path: '/dashboard',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'My Projects',
      path: '/projects',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'My Teams',
      path: '/personal/teams',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'My Tasks',
      path: '/tasks',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'Messages',
      path: '/messages',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      roles: ['user'],
      showUnread: true,
    },
    {
      name: 'Calendar',
      path: '/tasks/calendar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'Timeline',
      path: '/tasks/timeline',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'Sticky Notes',
      path: '/sticky-notes',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'Billing',
      path: '/billing',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      roles: ['user'],
    },
    {
      name: 'Marketing',
      path: '/marketing',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
      roles: ['platform_admin', 'super_admin'], // Platform admins only
      isPlatformOnly: true,
    },
    // Platform Admin-only items
    {
      name: 'System Users',
      path: '/users',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      roles: ['platform_admin', 'super_admin'],
      isPlatformOnly: true,
    },
    {
      name: 'Reports & Analytics',
      path: '/reports',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      roles: ['platform_admin', 'super_admin'],
      isPlatformOnly: true,
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      roles: ['user'],
    },
  ];

  const renderTooltip = (item, index) => {
    if (!collapsed || showTooltip !== index) return null;
    
    return (
      <div className="absolute left-16 bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg whitespace-nowrap z-50">
        {item.name}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-800 rotate-45"></div>
      </div>
    );
  };

  // Handle mouse down on resize handle - simplified for collapse/expand only
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle mouse move for simple collapse/expand
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const currentX = e.clientX;
    
    if (collapsed) {
      // When collapsed, any drag to the right should expand
      if (currentX > 80) { // Small threshold from the collapsed sidebar edge
        toggleCollapse();
        setIsDragging(false);
      }
    } else {
      // When expanded, drag left to collapse
      if (currentX < 120) {
        toggleCollapse();
        setIsDragging(false);
      }
    }
  }, [isDragging, collapsed, toggleCollapse]);

  // Handle mouse up to stop dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global event listeners for mouse events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={toggleSidebar}>
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" aria-hidden="true"></div>
        </div>
      )}

      {/* Sidebar */}
      <div 
        ref={sidebarRef}
        className={`
          bg-gradient-to-b from-blue-800 to-blue-900 transition-all duration-300 ease-in-out flex flex-col h-full
          ${collapsed ? 'w-16' : ''}
          fixed inset-y-0 left-0 z-50 md:relative md:z-auto
          ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        style={{
          width: collapsed ? '64px' : '191px'
        }}
      >
        {/* Header */}
        <div className={`h-16 flex items-center bg-blue-900 shadow-lg transition-all duration-300 ${
          collapsed ? 'justify-center px-2' : 'justify-between px-4'
        }`}>
          <Link to="/home" className="flex items-center hover:opacity-80 transition-opacity duration-200">
            <div className="w-8 h-8 rounded-lg overflow-hidden">
              <img 
                src="/icon.png" 
                alt="Tasklina Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            {!collapsed && (
              <span className="font-bold text-xl text-white ml-3">Tasklina</span>
            )}
          </Link>
          
          {/* Collapse toggle button - Hidden on mobile */}
          <button
            type="button"
            className="hidden md:block text-white hover:text-gray-200 focus:outline-none transition-colors duration-200"
            onClick={toggleCollapse}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-5 w-5 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Mobile close button */}
          <button
            type="button"
            className="md:hidden text-white hover:text-gray-200 focus:outline-none"
            onClick={toggleSidebar}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode Switch - Only show for users with multiple mode access */}
        {!collapsed && (
          <div className="px-3 py-2.5 border-b border-blue-700/80">
            <ModeSwitch variant="sidebar" shortSidebarLabel />
          </div>
        )}

        {/* Navigation - Scrollable with hidden scrollbar */}
        <nav className={`flex-1 overflow-y-auto mt-4 ${collapsed ? 'px-1' : 'px-2'}`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div className="space-y-1">
            {/* Regular navigation items */}
            {navigation
              .filter(item => !item.isPlatformOnly && hasRole(item.roles))
              .map((item, index) => (
                <div key={item.name} className="relative">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `group flex items-center ${collapsed ? 'justify-center' : ''} px-2 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isActive
                          ? 'bg-blue-700 text-white shadow-md'
                          : 'text-blue-100 hover:bg-blue-700 hover:text-white'
                      }`
                    }
                    onClick={(event) => {
                      if (window.innerWidth < 768) toggleSidebar();
                    }}
                    onMouseEnter={() => collapsed && setShowTooltip(index)}
                    onMouseLeave={() => collapsed && setShowTooltip(null)}
                  >
                    <span className={collapsed ? '' : 'mr-2'}>{item.icon}</span>
                    {!collapsed && <span>{item.name}</span>}
                    {item.showUnread && totalUnread > 0 && (
                      <span
                        className={`${collapsed ? 'absolute top-1 right-1' : 'ml-auto'} inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none`}
                      >
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </NavLink>
                  {renderTooltip(item, index)}
                </div>
              ))}

          </div>
        </nav>

        {/* Quick Actions - At bottom */}
        <div className={`mt-auto p-3 border-t border-blue-700 ${collapsed ? 'px-1' : ''}`}>
          <div className="relative">
            <NavLink
              to="/profile"
              className={`flex items-center ${collapsed ? 'justify-center' : ''} px-3 py-2 text-sm text-blue-100 hover:bg-blue-700 hover:text-white rounded-lg transition-colors duration-200`}
              onMouseEnter={() => collapsed && setShowTooltip('profile')}
              onMouseLeave={() => collapsed && setShowTooltip(null)}
            >
              <svg className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {!collapsed && 'My Profile'}
            </NavLink>
            {collapsed && showTooltip === 'profile' && (
              <div className="absolute left-16 bottom-2 bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg whitespace-nowrap z-50">
                My Profile
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-800 rotate-45"></div>
              </div>
            )}
          </div>
        </div>

        {/* Simple Drag Handle - Always visible for expand/collapse */}
        <div
          className={`absolute top-0 right-0 h-full w-2 cursor-col-resize group`}
          onMouseDown={handleMouseDown}
          style={{ zIndex: 60 }}
          title={collapsed ? "Drag right to expand sidebar" : "Drag left to collapse sidebar"}
        >
          {/* Minimal resize line - more visible when collapsed */}
          <div 
            className={`absolute top-0 right-0 h-full w-0.5 transition-all duration-200 ${
              isDragging 
                ? 'opacity-100 bg-blue-400' 
                : collapsed 
                  ? 'bg-blue-400 opacity-20 group-hover:opacity-80' 
                  : 'bg-blue-300 opacity-0 group-hover:opacity-70'
            }`}
          />
        </div>
      </div>
    </>
  );
};

export default IndividualSidebar;
